import { useEffect, useState, useCallback } from "react";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Select,
  Checkbox,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { btnPrimaryCls, btnSecondaryCls } from "components/tw/classes";
import { StatusBanner, useSaveWithToast } from "containers/settings/ui";
import FileCategorySingleSelect from "components/file-category-single-select/file-category-single-select";
import {
  createFileTemplate,
  updateFileTemplate,
  fetchFileTemplateById,
} from "./files-template-form.service";
import SearchableSeriesSelect from "components/searchable-series-select/searchable-series-select";
import SearchableFilesTypesSelect from "components/searchable-files-types-select/searchable-files-types-select";
import FilesTemplateMultiSelect from "components/file-template-multi-select/file-template-multi-select";

// `id` is the template being edited; without it the form creates a new one.
function FilesTemplateForm({ id, onSaved, onCancel }) {
  const { t } = useTranslation();
  const { save, saving, error } = useSaveWithToast();
  const { companyId } = useParams();
  const [name, setName] = useState("");
  // const [serial, setSerial] = useState('');
  const [series, setSeries] = useState("");
  const [category, setCategory] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [html, setHtml] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  // An existing template is fetched into empty fields; until then they must not be typed in or saved.
  const [loading, setLoading] = useState(Boolean(id));
  const [isApprovalRequired, setIsApprovalRequired] = useState(false);
  const [templateType, setTemplateType] = useState("");
  const [subject, setSubject] = useState("");
  const [notifyAccountancy, setNotifyAccountancy] = useState(false);
  const [signType, setSignType] = useState("");

  const handleSelectChange = useCallback((value) => setSubject(value), []);
  const [
    isValidFinancialApprovalRequired,
    setIsValidFinancialApprovalRequired,
  ] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [jsonViewMode, setJsonViewMode] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState("");

  const handleToggleJsonView = () => {
    if (!jsonViewMode) {
      setJsonText(JSON.stringify(customFields, null, 2));
      setJsonError("");
      setJsonViewMode(true);
    } else {
      try {
        const parsed = JSON.parse(jsonText);
        if (!Array.isArray(parsed)) {
          setJsonError(t("json_must_be_array"));
          return;
        }
        setCustomFields(parsed);
        setJsonError("");
        setJsonViewMode(false);
      } catch (e) {
        setJsonError(`${t("invalid_json")}: ${e.message}`);
      }
    }
  };

  const hasInvalidNumberRange = customFields.some(
    (field) =>
      field.type === "number" &&
      field.min !== null &&
      field.min !== undefined &&
      field.max !== null &&
      field.max !== undefined &&
      field.min > field.max
  );

  const handleFormSubmit = async () => {
    if (hasInvalidNumberRange) {
      return;
    }
    const categoryData = category.length ? category[0] : null;
    const formData = {
      id,
      name,
      category: categoryData,
      customFields,
      templateType,
      // serial,
      series,
      html,
      isApprovalRequired,
      isValidFinancialApprovalRequired,
      relatedTo: selectedTemplates,
      subject,
      notifyAccountancy,
      signType,
    };
    const action = id ? updateFileTemplate : createFileTemplate;
    const isSaved = await save(() => action(formData, companyId));
    if (isSaved) {
      onSaved();
    }
  };

  const fetchData = useCallback(
    async (id) => {
      const data = await fetchFileTemplateById(companyId, id);

      setName(data.name);
      setCategory(data.category ? [data.category] : []);
      // setSerial(data.serial);
      setSeries(data.series);
      setCustomFields(data.customFields || []); // Default to empty array if null/undefined
      setHtml(data.html);
      setIsApprovalRequired(data.isApprovalRequired);
      setTemplateType(data.templateType);
      setIsValidFinancialApprovalRequired(
        data.isValidFinancialApprovalRequired
      );
      setSelectedTemplates(data.relatedTo);
      setSubject(data.subject);
      setNotifyAccountancy(data.notifyAccountancy);
      setSignType(data.signType);
    },
    [companyId]
  );

  useEffect(() => {
    if (id) {
      fetchData(id)
        .catch(() => setLoadFailed(true))
        .finally(() => setLoading(false));
    }
  }, [id, fetchData]);

  const addCustomField = () => {
    setCustomFields([
      ...customFields,
      {
        id: Date.now(),
        type: "string",
        label: "",
        name: "",
        options: [],
        required: false,
        default_today: false,
        minLength: "",
      },
    ]);
  };
  const signCollection = [
    { label: "-", value: "" },
    { label: t("click"), value: "Click" },
    { label: "Dokobit", value: "Dokobit" },
    { label: "Signontab", value: "Signontab" },
  ];
  const updateCustomField = (index, key, value) => {
    const updatedFields = customFields.map((field, i) =>
      i === index ? { ...field, [key]: value } : field
    );
    setCustomFields(updatedFields);
  };

  const updateFieldOptions = (index, optionIndex, value) => {
    const updatedFields = customFields.map((field, i) => {
      if (i === index) {
        const updatedOptions = field.options.map((option, j) =>
          j === optionIndex ? value : option
        );
        return { ...field, options: updatedOptions };
      }
      return field;
    });
    setCustomFields(updatedFields);
  };

  const addOptionToField = (index) => {
    const updatedFields = customFields.map((field, i) => {
      if (i === index) {
        return { ...field, options: [...field.options, ""] };
      }
      return field;
    });
    setCustomFields(updatedFields);
  };

  const removeOptionFromField = (index, optionIndex) => {
    const updatedFields = customFields.map((field, i) => {
      if (i === index) {
        return {
          ...field,
          options: field.options.filter((_, j) => j !== optionIndex),
        };
      }
      return field;
    });
    setCustomFields(updatedFields);
  };

  const removeCustomField = (index) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  const handleChange = useCallback(
    (setter) => (newChecked) => {
      setter(newChecked);
    },
    []
  );

  const handleTemplateChange = (newSelection) => {
    setSelectedTemplates(newSelection);
  };

  const subjectOptions = [
    { label: "-", value: "" },
    { label: "user", value: "user" },
    { label: "vacation", value: "vacation" },
    { label: "unpaid leave", value: "unpaid leave" },
    { label: "additional day off", value: "additional day off" },
    {
      label: "additional rest time (September 1)",
      value: "additional rest time",
    },
    { label: "paternity leave", value: "paternity leave" },
    { label: "business trip leave", value: "business trip leave" },
    { label: "remote work", value: "remote work" },
    { label: "assets", value: "assets" },
    { label: "customer", value: "customer" },
    { label: "fuel", value: "fuel" },
    { label: "finance", value: "finance" },
    { label: "business trip", value: "business trip" },
  ];

  if (loadFailed) {
    return (
      <StatusBanner tone="error" title={t("settings_templates_load_failed")} />
    );
  }

  if (loading) {
    return <p className="text-sm text-gray-500">{t("loading")}</p>;
  }

  return (
    <Form className="customer-form_form">
      <FormLayout>
        <Select
          label={t("type")}
          options={subjectOptions}
          onChange={handleSelectChange}
          value={subject}
          requiredIndicator={true}
        />
        <TextField
          label={t("files_template_name")}
          id="name"
          name="name"
          value={name}
          onChange={setName}
          requiredIndicator={true}
        />
        <SearchableFilesTypesSelect
          isRequired={true}
          onChange={setTemplateType}
          value={templateType}
        />
        <SearchableSeriesSelect onChange={setSeries} value={series} />
        <FileCategorySingleSelect
          onChange={setCategory}
          value={category}
          isRequired={true}
        />
        <FilesTemplateMultiSelect
          value={selectedTemplates}
          onChange={handleTemplateChange}
          disabled={false}
        />
        <TextField
          label={t("files_template_html")}
          id="html"
          name="html"
          value={html}
          multiline={10}
          maxHeight={400}
          onChange={setHtml}
        />

        {jsonViewMode ? (
          <BlockStack gap="200">
            <TextField
              label="Custom Fields JSON"
              value={jsonText}
              onChange={setJsonText}
              multiline={20}
              autoComplete="off"
              monospaced
            />
            {jsonError && (
              <Text tone="critical" as="p" variant="bodySm">
                {jsonError}
              </Text>
            )}
          </BlockStack>
        ) : null}

        {!jsonViewMode &&
          customFields &&
          customFields.length > 0 &&
          customFields.map((field, index) => (
            <Card key={field.id} sectioned>
              <InlineGrid columns="1fr auto">
                <Text as="h2" variant="headingSm">
                  #{field.id}
                </Text>
                <Button destructive onClick={() => removeCustomField(index)}>
                  {t("files_template_custom_remove")}
                </Button>
              </InlineGrid>
              <InlineGrid gap="4">
                <Select
                  label={t("files_template_custom_type")}
                  options={[
                    {
                      label: t("files_template_custom_type_string"),
                      value: "string",
                    },
                    {
                      label: t("files_template_custom_type_number"),
                      value: "number",
                    },
                    {
                      label: t("files_template_custom_type_textarea"),
                      value: "textarea",
                    },
                    {
                      label: t("files_template_custom_type_date"),
                      value: "date",
                    },
                    {
                      label: t("files_template_custom_type_select"),
                      value: "select",
                    },
                    {
                      label: t("files_template_custom_type_checkbox"),
                      value: "checkbox",
                    },
                    {
                      label: t("files_template_custom_type_products_or_groups"),
                      value: "products_or_groups",
                    },
                    {
                      label: t("files_template_custom_type_address"),
                      value: "address",
                    },
                    {
                      label: t("files_template_custom_type_user"),
                      value: "user",
                    },
                    {
                      label: t("files_template_custom_type_file"),
                      value: "file",
                    },
                    {
                      label: t("files_template_custom_type_signature"),
                      value: "signature",
                    },
                    {
                      label: t("files_template_custom_type_country"),
                      value: "country",
                    },
                    {
                      label: t("files_template_custom_type_business_trip"),
                      value: "business_trip",
                    },
                  ]}
                  value={field.type}
                  onChange={(value) => updateCustomField(index, "type", value)}
                />
                <Checkbox
                  label={t("files_template_custom_required")}
                  checked={field.required}
                  onChange={(checked) =>
                    updateCustomField(index, "required", checked)
                  }
                />
                {field.type === "checkbox" && (
                  <Checkbox
                    label={t("files_template_custom_checkbox_multi")}
                    checked={!!field.isMulti}
                    onChange={(checked) =>
                      updateCustomField(index, "isMulti", checked)
                    }
                  />
                )}
                {(field.type === "string" || field.type === "textarea") && (
                  <>
                    <TextField
                      label={t("files_template_custom_min_length")}
                      type="number"
                      value={
                        field.minLength !== null &&
                        field.minLength !== undefined
                          ? String(field.minLength)
                          : ""
                      }
                      onChange={(value) => {
                        const numValue = parseInt(value, 10);
                        if (isNaN(numValue) || numValue < 1) {
                          updateCustomField(index, "minLength", null);
                        } else {
                          updateCustomField(index, "minLength", numValue);
                        }
                      }}
                    />
                    <TextField
                      label={t("files_template_custom_helper_text")}
                      value={field.helperText || ""}
                      onChange={(value) =>
                        updateCustomField(index, "helperText", value)
                      }
                    />
                  </>
                )}
                {field.type === "date" && (
                  <Checkbox
                    label={t("default_today")}
                    checked={field.default_today}
                    onChange={(checked) =>
                      updateCustomField(index, "default_today", checked)
                    }
                  />
                )}
                {field.type === "number" && (
                  <>
                    <TextField
                      label={t("files_template_custom_number_min")}
                      type="number"
                      value={
                        field.min !== null && field.min !== undefined
                          ? String(field.min)
                          : ""
                      }
                      onChange={(value) => {
                        const trimmed = value.trim();
                        if (trimmed === "") {
                          updateCustomField(index, "min", null);
                        } else {
                          const numValue = parseFloat(trimmed);
                          updateCustomField(
                            index,
                            "min",
                            isNaN(numValue) ? null : numValue
                          );
                        }
                      }}
                    />
                    <TextField
                      label={t("files_template_custom_number_max")}
                      type="number"
                      value={
                        field.max !== null && field.max !== undefined
                          ? String(field.max)
                          : ""
                      }
                      onChange={(value) => {
                        const trimmed = value.trim();
                        if (trimmed === "") {
                          updateCustomField(index, "max", null);
                        } else {
                          const numValue = parseFloat(trimmed);
                          updateCustomField(
                            index,
                            "max",
                            isNaN(numValue) ? null : numValue
                          );
                        }
                      }}
                    />
                    {field.min !== null &&
                      field.min !== undefined &&
                      field.max !== null &&
                      field.max !== undefined &&
                      field.min > field.max && (
                        <Text as="p" tone="critical" variant="bodySm">
                          {t("number_min_must_be_less_than_or_equal_to_max")}
                        </Text>
                      )}
                  </>
                )}
              </InlineGrid>
              <BlockStack gap="4">
                {field.type !== "products" &&
                  field.type !== "product_group" &&
                  field.type !== "products_or_groups" && (
                    <BlockStack gap="4">
                      <TextField
                        label={t("files_template_custom_name")}
                        value={field.name}
                        onChange={(value) =>
                          updateCustomField(index, "name", value)
                        }
                      />
                      <TextField
                        label={t("files_template_custom_label")}
                        value={field.label}
                        onChange={(value) =>
                          updateCustomField(index, "label", value)
                        }
                      />
                    </BlockStack>
                  )}
                {(field.type === "select" || field.type === "checkbox") && (
                  <BlockStack gap="4">
                    {field.options.map((option, optionIndex) => (
                      <InlineStack key={optionIndex} gap="4" blockAlign="end">
                        <TextField
                          label={`${t(
                            "files_template_custom_type_select_option"
                          )} ${optionIndex + 1}`}
                          value={option}
                          onChange={(value) =>
                            updateFieldOptions(index, optionIndex, value)
                          }
                        />
                        <div>
                          <Button
                            destructive
                            onClick={() =>
                              removeOptionFromField(index, optionIndex)
                            }
                          >
                            {t("files_template_custom_remove_option")}
                          </Button>
                        </div>
                      </InlineStack>
                    ))}
                    <Button onClick={() => addOptionToField(index)}>
                      {t("files_template_custom_add_option")}
                    </Button>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          ))}
        <Select
          label={t("select_sign_type")}
          options={signCollection}
          value={signType}
          onChange={(value) => setSignType(value)}
        />

        <Checkbox
          label={t("is_approval_required")}
          checked={isApprovalRequired}
          onChange={handleChange(setIsApprovalRequired)}
        />
        <Checkbox
          label={t("is_valid_financial_approval_required")}
          checked={isValidFinancialApprovalRequired}
          onChange={handleChange(setIsValidFinancialApprovalRequired)}
        />
        <Checkbox
          label={t("notify_accountancy")}
          checked={notifyAccountancy}
          onChange={handleChange(setNotifyAccountancy)}
        />
        <InlineStack gap="300">
          {!jsonViewMode && (
            <Button onClick={addCustomField}>
              {t("files_template_custom_add")}
            </Button>
          )}
          <Button onClick={handleToggleJsonView}>
            {jsonViewMode ? t("builder_view") : t("json_view")}
          </Button>
        </InlineStack>
      </FormLayout>
      {/* The form is long, so on a phone the buttons stay at the bottom edge of the screen
          instead of waiting after the last custom field. They sit outside FormLayout because
          a sticky box cannot leave the FormLayout item that wraps it. The inset is 0, not the phone
          bar's height: the page already has that height as bottom padding and a sticky inset counts
          from inside it. */}
      <div className="mt-4 max-md:sticky max-md:bottom-0 max-md:z-10 max-md:border-t max-md:border-gray-200 max-md:bg-white max-md:py-2">
        {error && (
          <p role="alert" className="mb-4 text-sm text-red-600 max-md:mb-2">
            {error}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={btnPrimaryCls}
            onClick={handleFormSubmit}
            disabled={
              saving ||
              !name ||
              !category ||
              !html ||
              !subject ||
              hasInvalidNumberRange
            }
          >
            {t("settings_save")}
          </button>
          <button
            type="button"
            className={btnSecondaryCls}
            onClick={onCancel}
            disabled={saving}
          >
            {t("settings_cancel")}
          </button>
        </div>
      </div>
    </Form>
  );
}

export default FilesTemplateForm;
